"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  catalogFiltersApiQuery,
  categoryPageHref,
  parseCatalogSearchParams,
  type CatalogQuery,
} from "~/lib/catalog-query";
import type { CatalogPriceRange, CatalogVendor } from "~/network/catalog-filters";

function categorySlugFromPath(pathname: string): string | null {
  const m = /^\/category\/([^/]+)/.exec(pathname);
  return m?.[1] ?? null;
}

function formatMoney(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

type PricePreset = { label: string; min?: number; max?: number };

const PRICE_BUCKET_PRESETS: PricePreset[] = [
  { label: "All prices" },
  { label: "Under $25", max: 25 },
  { label: "$25 – $50", min: 25, max: 50 },
  { label: "$50 – $100", min: 50, max: 100 },
  { label: "$100 – $200", min: 100, max: 200 },
  { label: "$200+", min: 200 },
];

function buildPricePresets(range: CatalogPriceRange): PricePreset[] {
  const lo = Math.floor(range.min_filtered_price);
  const hi = Math.ceil(range.max_filtered_price);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [];
  return PRICE_BUCKET_PRESETS;
}

function filtersMatchPrice(
  query: CatalogQuery,
  preset: PricePreset,
): boolean {
  if (preset.min == null && preset.max == null) {
    return query.price_min == null && query.price_max == null;
  }
  return query.price_min === preset.min && query.price_max === preset.max;
}

export function CatalogFilters({ onNavigate }: { onNavigate?: () => void }) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const categorySlug = categorySlugFromPath(pathname);

  const catalogQuery = useMemo(
    () => parseCatalogSearchParams(searchParams),
    [searchParams],
  );

  const [vendors, setVendors] = useState<CatalogVendor[]>([]);
  const [priceRange, setPriceRange] = useState<CatalogPriceRange | null>(null);
  const [loading, setLoading] = useState(false);
  const [draftMin, setDraftMin] = useState("");
  const [draftMax, setDraftMax] = useState("");

  const apiQuery = useMemo(() => {
    if (!categorySlug) return "";
    return catalogFiltersApiQuery({ ...catalogQuery, category: categorySlug });
  }, [categorySlug, catalogQuery]);

  useEffect(() => {
    if (catalogQuery.price_min != null) {
      setDraftMin(String(catalogQuery.price_min));
    } else if (priceRange) {
      setDraftMin(String(priceRange.min_filtered_price));
    } else {
      setDraftMin("");
    }
    if (catalogQuery.price_max != null) {
      setDraftMax(String(catalogQuery.price_max));
    } else if (priceRange) {
      setDraftMax(String(priceRange.max_filtered_price));
    } else {
      setDraftMax("");
    }
  }, [catalogQuery.price_min, catalogQuery.price_max, priceRange]);

  useEffect(() => {
    if (!categorySlug) {
      setVendors([]);
      setPriceRange(null);
      return;
    }

    const ac = new AbortController();
    setLoading(true);
    const qs = apiQuery ? `?${apiQuery}` : "";

    Promise.all([
      fetch(`/api/businesses${qs}`, { signal: ac.signal }),
      fetch(`/api/product/prices${qs}`, { signal: ac.signal }),
    ])
      .then(async ([vendorsRes, pricesRes]) => {
        const vendorsJson = (await vendorsRes.json()) as { data?: CatalogVendor[] };
        const pricesJson = (await pricesRes.json()) as { data?: CatalogPriceRange | null };
        if (ac.signal.aborted) return;
        setVendors(
          Array.isArray(vendorsJson.data)
            ? vendorsJson.data.filter((v) => v.count > 0)
            : [],
        );
        setPriceRange(pricesJson.data ?? null);
      })
      .catch((e) => {
        if ((e as Error).name === "AbortError") return;
        setVendors([]);
        setPriceRange(null);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });

    return () => ac.abort();
  }, [categorySlug, apiQuery]);

  const navigate = useCallback(
    (next: CatalogQuery) => {
      if (!categorySlug) return;
      router.push(categoryPageHref(categorySlug, next));
      onNavigate?.();
    },
    [categorySlug, router, onNavigate],
  );

  const toggleVendor = useCallback(
    (vendorId: string) => {
      const current = new Set(catalogQuery.business_ids ?? []);
      if (current.has(vendorId)) current.delete(vendorId);
      else current.add(vendorId);
      const business_ids = [...current];
      navigate({
        ...catalogQuery,
        page: undefined,
        business_ids: business_ids.length ? business_ids : undefined,
      });
    },
    [catalogQuery, navigate],
  );

  const applyPrice = useCallback(() => {
    const min = draftMin.trim() === "" ? undefined : parseInt(draftMin, 10);
    const max = draftMax.trim() === "" ? undefined : parseInt(draftMax, 10);
    if (
      (min != null && (!Number.isFinite(min) || min < 0)) ||
      (max != null && (!Number.isFinite(max) || max < 0))
    ) {
      return;
    }
    navigate({
      ...catalogQuery,
      page: undefined,
      price_min: min,
      price_max: max,
    });
  }, [catalogQuery, draftMin, draftMax, navigate]);

  const clearFilters = useCallback(() => {
    if (!categorySlug) return;
    navigate({});
  }, [categorySlug, navigate]);

  const pricePresets = useMemo(
    () => (priceRange ? buildPricePresets(priceRange) : []),
    [priceRange],
  );

  const hasActiveFilters =
    (catalogQuery.business_ids?.length ?? 0) > 0 ||
    catalogQuery.price_min != null ||
    catalogQuery.price_max != null;

  if (!categorySlug) return null;

  return (
    <div className="mt-6 border-t border-stone-200 pt-5">
      <div className="mb-3 flex items-center justify-between px-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">
          Filters
        </span>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs font-medium text-brand-red hover:underline"
          >
            Clear
          </button>
        ) : null}
      </div>

      {loading ? (
        <p className="px-3 text-xs text-stone-500">Loading filters…</p>
      ) : null}

      <section className="px-3" aria-labelledby="catalog-vendors-heading">
        <h3
          id="catalog-vendors-heading"
          className="mb-2 text-sm font-semibold text-stone-800"
        >
          Vendors
        </h3>
        {vendors.length === 0 && !loading ? (
          <p className="text-xs text-stone-500">No vendors for this selection.</p>
        ) : (
          <ul className="max-h-48 space-y-1 overflow-y-auto">
            {vendors.map((v) => {
              const checked = catalogQuery.business_ids?.includes(v.id) ?? false;
              return (
                <li key={v.id}>
                  <label className="flex cursor-pointer items-start gap-2 rounded-md py-1 text-sm text-stone-700 hover:bg-stone-200/60">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-stone-300 text-brand-red focus:ring-brand-red"
                      checked={checked}
                      onChange={() => toggleVendor(v.id)}
                    />
                    <span className="min-w-0 flex-1 leading-snug">
                      {v.name}
                      <span className="text-stone-400"> ({v.count})</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="mt-5 px-3" aria-labelledby="catalog-price-heading">
        <h3
          id="catalog-price-heading"
          className="mb-2 text-sm font-semibold text-stone-800"
        >
          Price
        </h3>
        {priceRange ? (
          <p className="mb-2 text-xs text-stone-500">
            {formatMoney(priceRange.min_filtered_price)} –{" "}
            {formatMoney(priceRange.max_filtered_price)}
          </p>
        ) : null}

        {pricePresets.length > 0 ? (
          <ul className="mb-3 space-y-0.5">
            {pricePresets.map((preset) => {
              const active = filtersMatchPrice(catalogQuery, preset);
              const key = `${preset.label}-${preset.min ?? ""}-${preset.max ?? ""}`;
              return (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() =>
                      navigate({
                        ...catalogQuery,
                        page: undefined,
                        price_min: preset.min,
                        price_max: preset.max,
                      })
                    }
                    className={
                      "w-full rounded-md px-2 py-1.5 text-left text-sm transition " +
                      (active
                        ? "bg-brand-red/10 font-medium text-brand-red"
                        : "text-stone-700 hover:bg-stone-200/60")
                    }
                  >
                    {preset.label}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}

        <div className="flex items-center gap-2">
          <label className="sr-only" htmlFor="catalog-price-min">
            Minimum price
          </label>
          <input
            id="catalog-price-min"
            type="number"
            min={0}
            inputMode="numeric"
            placeholder="Min"
            value={draftMin}
            onChange={(e) => setDraftMin(e.target.value)}
            className="w-full min-w-0 rounded-md border border-stone-300 px-2 py-1.5 text-sm"
          />
          <span className="text-stone-400">–</span>
          <label className="sr-only" htmlFor="catalog-price-max">
            Maximum price
          </label>
          <input
            id="catalog-price-max"
            type="number"
            min={0}
            inputMode="numeric"
            placeholder="Max"
            value={draftMax}
            onChange={(e) => setDraftMax(e.target.value)}
            className="w-full min-w-0 rounded-md border border-stone-300 px-2 py-1.5 text-sm"
          />
        </div>
        <button
          type="button"
          onClick={applyPrice}
          className="mt-2 w-full rounded-md border border-stone-300 bg-white px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
        >
          Apply
        </button>
      </section>
    </div>
  );
}
